import { useEffect, useMemo, useCallback } from "react";
import type { CellClickArgs } from "react-data-grid";
import { useParams, useNavigate, useLocation } from "react-router-dom";

import Table from "components/Table/Table";
import ButtonIcon from "components/ButtonIcon/ButtonIcon";
import {
  useGetPoliciesByRreIdQuery,
  useGetRreValidationsQuery,
} from "services/rre-api";
import { useDeletePolicyMutation } from "services/policy-api";
import RREPageContainer from "./RREPageContainer";
import { CreatePolicyModal } from "components/Modals";
import { setShowAddPolicyModal } from "reducers/rre";
import type { PolicyType } from "types/DataTypes";
import { useSelector, useDispatch } from "utils/redux";
import { setIsLoading, setValidationState } from "reducers/main";

const RREPoliciesTable = () => {
  const { rreId } = useParams<{ rreId?: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [deletePolicy] = useDeletePolicyMutation();

  const dispatch = useDispatch();
  const showAddPolicyModal = useSelector(
    (state: { rre: { showAddPolicyModal: boolean } }) =>
      state.rre.showAddPolicyModal,
  );

  const handleCellClick = (args: CellClickArgs<PolicyType, unknown>) => {
    navigate(`${location.pathname}/${args.row.id}/overview`);
  };

  const handleCloseModal = useCallback(() => {
    dispatch(setShowAddPolicyModal(false));
  }, [dispatch]);

  const { data, isLoading } = useGetPoliciesByRreIdQuery(
    { rreId },
    { skip: !rreId },
  );

  const handleDeleteClick = useCallback(
    async (row) => {
      await deletePolicy({ id: row.id });
    },
    [deletePolicy],
  );

  useEffect(() => {
    dispatch(setIsLoading(isLoading));
  }, [dispatch, isLoading]);

  const { data: rreValidation } = useGetRreValidationsQuery(
    { id: rreId },
    { skip: !rreId },
  );

  useEffect(() => {
    if (rreValidation) {
      const validationData = Object.values(rreValidation).flat();
      const errors = validationData.filter((item) => item.level === "ERROR");
      const warnings = validationData.filter((item) => item.level === "WARN");
      dispatch(
        setValidationState({
          errors: errors,
          warnings: warnings,
        }),
      );
    }
  }, [dispatch, rreValidation]);

  const columns = useMemo(() => {
    return [
      { key: "name", name: "Name" },
      { key: "policy_number", name: "Policy Number" },
      { key: "legal_name", name: "Legal Name" },
      { key: "dba_name", name: "DBA Name" },
      { key: "plan_insurance_type_verbose", name: "Plan Insurance Type" },
      { key: "is_self_insured_verbose", name: "Is Self Insured" },
      { key: "self_insured_type_verbose", name: "Self Insured Type" },
      {
        key: "actions",
        name: "Actions",
        width: "10%",
        renderCell(props) {
          return (
            <div className="action-column">
              <ButtonIcon
                className="btn-icon delete"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteClick(props.row);
                }}
              >
                delete
              </ButtonIcon>
            </div>
          );
        },
      },
    ];
  }, []);

  return (
    <RREPageContainer>
      <CreatePolicyModal
        isOpen={showAddPolicyModal}
        onClose={handleCloseModal}
      />
      <Table
        className="rre-policies-table"
        columns={columns}
        rows={data?.results || []}
        rowKeyGetter={(row: PolicyType) => row.id}
        onCellClick={handleCellClick}
      />
    </RREPageContainer>
  );
};

export default RREPoliciesTable;
